import { Fragment, ReactNode } from 'react'

import { LabelValue } from '~/components/component-library'
import {
  DecodedCallArg,
  DecodedCallSummary,
} from '~/components/txn-display/state-change/types'
import { shortenAddress } from '~/utils/addresses'
import { cn } from '~/utils/tailwind-utils'

function formatArgValue(value: unknown): string {
  if (value === null || value === undefined) return String(value)
  if (typeof value === 'string') return value
  if (typeof value === 'bigint') return value.toString()
  // For objects/arrays, use a replacer to handle nested BigInts and pretty-print with 2-space indent
  return JSON.stringify(
    value,
    (_key, val) => (typeof val === 'bigint' ? val.toString() : val),
    2,
  )
}

function isJsonValue(value: unknown): boolean {
  return (
    value !== null &&
    value !== undefined &&
    typeof value !== 'string' &&
    typeof value !== 'bigint' &&
    typeof value === 'object'
  )
}

export function DecodedCalldata({
  calls,
}: {
  calls: DecodedCallSummary[]
}): ReactNode {
  if (calls.length === 0) {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-gray-100 p-3.5 dark:bg-gray-900">
        <span className="text-sm text-secondary">No decoded calls found</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-gray-100 p-3.5 dark:bg-gray-900">
      {calls.map((call, index) => (
        <Fragment key={call.id}>
          {index > 0 && (
            <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />
          )}
          <DecodedCallItem call={call} />
        </Fragment>
      ))}
    </div>
  )
}

function DecodedCallItem({ call }: { call: DecodedCallSummary }): ReactNode {
  return (
    <div className="flex flex-col space-y-2">
      <ToRow target={call.target} />
      <ValueRow value={call.value} />
      <DataRow data={call.data} />
      <FunctionRow functionName={call.functionName} />
      <ArgsRow args={call.args} />
    </div>
  )
}

function ToRow({
  target,
}: {
  target: DecodedCallSummary['target']
}): ReactNode {
  return (
    <LabelValue
      size="small"
      label="To"
      copyable={target}
      value={shortenAddress(target, 4)}
    />
  )
}

function ValueRow({
  value,
}: {
  value: DecodedCallSummary['value']
}): ReactNode {
  const valueString = value?.toString() ?? '0'

  return <LabelValue size="small" label="Value" copyable value={valueString} />
}

function FunctionRow({
  functionName,
}: {
  functionName: DecodedCallSummary['functionName']
}): ReactNode {
  if (!functionName) return null
  return (
    <LabelValue size="small" label="Function" value={`${functionName}()`} />
  )
}

function ArgsRow({ args }: { args: DecodedCallSummary['args'] }): ReactNode {
  if (!args || args.length === 0) return null

  const summaryValue = args.length > 1 ? `${args.length} args` : args[0].name

  return (
    <LabelValue
      size="small"
      label="Args"
      isCollapsible
      defaultOpen={false}
      value={summaryValue}
    >
      <div className="flex flex-col gap-px overflow-hidden rounded-md">
        {args.map((arg, index) => (
          <ArgRow key={`${arg.name}-${index}`} arg={arg} />
        ))}
      </div>
    </LabelValue>
  )
}

function ArgRow({ arg }: { arg: DecodedCallArg }): ReactNode {
  const formattedValue = formatArgValue(arg.value)
  const isJson = isJsonValue(arg.value)

  return (
    <div className="bg-gray-200 p-2.5 dark:bg-gray-800">
      <LabelValue
        size="small"
        label={arg.name}
        copyable={formattedValue}
        value={
          <span
            className={cn({
              'flex-1 whitespace-pre-wrap break-all text-left font-mono text-xs':
                isJson,
              'flex-1 break-all text-right': !isJson,
            })}
          >
            {formattedValue}
          </span>
        }
      />
    </div>
  )
}

function DataRow({ data }: { data: DecodedCallSummary['data'] }): ReactNode {
  if (!data) return null

  return (
    <LabelValue
      size="small"
      label="Data"
      copyable={data}
      value={
        <span className="line-clamp-1 max-w-64 flex-1 break-all text-right">
          {data}
        </span>
      }
    />
  )
}
